import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  HiChevronLeft,
  HiChevronRight,
  HiCheckCircle,
  HiDocumentArrowDown,
  HiArrowDownTray,
  HiDocumentText,
  HiCloudArrowUp,
  HiSparkles,
} from 'react-icons/hi2';
import { useAuth } from '../../context/AuthContext';
import { useUserRole } from '../../context/UserRoleContext';
import { getGTMPlan, saveGTMPlan, canEdit } from '../../services/commercialisationService';
import { firestore, storage } from '../../database/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import CommentSection from './CommentSection';
import GTMImportModal from './GTMImportModal';
import GTMTaskTracker from './GTMTaskTracker';
import GTMInsights from './GTMInsights';

const STEPS = [
  {
    id: 'executiveSummary',
    title: 'Executive Summary',
    description: 'High-level overview of your GTM strategy',
    fields: [
      { id: 'businessName', label: 'Business Name', type: 'text', required: true },
      { id: 'mission', label: 'Mission Statement', type: 'textarea', required: true },
      { id: 'vision', label: 'Vision Statement', type: 'textarea', required: true },
      { id: 'valueProposition', label: 'Value Proposition', type: 'textarea', required: true },
      { id: 'targetMarket', label: 'Target Market Overview', type: 'textarea', required: true },
    ],
  },
  {
    id: 'businessContext',
    title: 'Business Context & Strategic Intent',
    description: 'Describe your business context and strategic goals',
    fields: [
      { id: 'businessModel', label: 'Business Model', type: 'textarea', required: true },
      { id: 'strategicGoals', label: 'Strategic Goals', type: 'textarea', required: true },
      { id: 'keyObjectives', label: 'Key Objectives (3-5)', type: 'textarea', required: true },
      { id: 'successMetrics', label: 'Success Metrics', type: 'textarea', required: true },
      { id: 'timeline', label: 'Timeline Overview', type: 'textarea', required: true },
    ],
  },
  {
    id: 'productReadiness',
    title: 'Product Readiness & Development Overview',
    description: 'Assess your product readiness for market',
    fields: [
      {
        id: 'productStatus',
        label: 'Product Development Status',
        type: 'textarea',
        required: true,
      },
      { id: 'features', label: 'Key Features & Capabilities', type: 'textarea', required: true },
      { id: 'differentiators', label: 'Product Differentiators', type: 'textarea', required: true },
      {
        id: 'technicalInfrastructure',
        label: 'Technical Infrastructure',
        type: 'textarea',
        required: true,
      },
      {
        id: 'roadmap',
        label: 'Product Roadmap (Next 12 months)',
        type: 'textarea',
        required: true,
      },
    ],
  },
  {
    id: 'marketValidation',
    title: 'Market Validation & Opportunity Analysis',
    description: 'Analyze market validation and opportunities',
    fields: [
      { id: 'marketSize', label: 'Total Addressable Market (TAM)', type: 'text', required: true },
      { id: 'marketGrowth', label: 'Market Growth Rate', type: 'text', required: true },
      {
        id: 'customerValidation',
        label: 'Customer Validation Results',
        type: 'textarea',
        required: true,
      },
      {
        id: 'marketTrends',
        label: 'Market Trends & Opportunities',
        type: 'textarea',
        required: true,
      },
      {
        id: 'marketChallenges',
        label: 'Market Challenges & Risks',
        type: 'textarea',
        required: true,
      },
    ],
  },
  {
    id: 'targetMarket',
    title: 'Target Market & Buyer Personas',
    description: 'Define your target market and buyer personas',
    fields: [
      { id: 'primaryPersona', label: 'Primary Buyer Persona', type: 'textarea', required: true },
      {
        id: 'secondaryPersona',
        label: 'Secondary Buyer Persona',
        type: 'textarea',
        required: false,
      },
      {
        id: 'customerNeeds',
        label: 'Customer Needs & Pain Points',
        type: 'textarea',
        required: true,
      },
      {
        id: 'buyingBehavior',
        label: 'Buying Behavior & Decision Process',
        type: 'textarea',
        required: true,
      },
      { id: 'customerSegments', label: 'Customer Segments', type: 'textarea', required: true },
    ],
  },
  {
    id: 'competitiveAnalysis',
    title: 'Competitive & Situational Analysis',
    description: 'Analyze your competition and market position',
    fields: [
      { id: 'competitors', label: 'Key Competitors', type: 'textarea', required: true },
      {
        id: 'competitiveAdvantage',
        label: 'Competitive Advantage',
        type: 'textarea',
        required: true,
      },
      { id: 'swot', label: 'SWOT Analysis', type: 'textarea', required: true },
      { id: 'marketPosition', label: 'Market Position', type: 'textarea', required: true },
      {
        id: 'differentiationStrategy',
        label: 'Differentiation Strategy',
        type: 'textarea',
        required: true,
      },
    ],
  },
  {
    id: 'valueProposition',
    title: 'Value Proposition & Positioning',
    description: 'Define your value proposition and positioning',
    fields: [
      { id: 'coreValue', label: 'Core Value Proposition', type: 'textarea', required: true },
      { id: 'positioning', label: 'Brand Positioning', type: 'textarea', required: true },
      { id: 'messaging', label: 'Key Messaging', type: 'textarea', required: true },
      { id: 'proofPoints', label: 'Proof Points & Evidence', type: 'textarea', required: true },
      { id: 'brandPersonality', label: 'Brand Personality', type: 'textarea', required: true },
    ],
  },
  {
    id: 'pricing',
    title: 'Pricing & Commercial Model',
    description: 'Define your pricing strategy and commercial model',
    fields: [
      { id: 'pricingModel', label: 'Pricing Model', type: 'textarea', required: true },
      { id: 'pricingStrategy', label: 'Pricing Strategy', type: 'textarea', required: true },
      { id: 'pricePoints', label: 'Price Points & Tiers', type: 'textarea', required: true },
      { id: 'revenueModel', label: 'Revenue Model', type: 'textarea', required: true },
      { id: 'unitEconomics', label: 'Unit Economics', type: 'textarea', required: true },
    ],
  },
  {
    id: 'marketing',
    title: 'Marketing & Brand Strategy',
    description: 'Outline your marketing and brand strategy',
    fields: [
      { id: 'marketingChannels', label: 'Marketing Channels', type: 'textarea', required: true },
      { id: 'contentStrategy', label: 'Content Strategy', type: 'textarea', required: true },
      { id: 'brandGuidelines', label: 'Brand Guidelines', type: 'textarea', required: true },
      {
        id: 'marketingBudget',
        label: 'Marketing Budget & Allocation',
        type: 'textarea',
        required: true,
      },
      { id: 'campaigns', label: 'Marketing Campaigns', type: 'textarea', required: true },
    ],
  },
  {
    id: 'distribution',
    title: 'Distribution Channels & Partnerships',
    description: 'Define distribution channels and partnerships',
    fields: [
      {
        id: 'distributionChannels',
        label: 'Distribution Channels',
        type: 'textarea',
        required: true,
      },
      { id: 'partnerships', label: 'Key Partnerships', type: 'textarea', required: true },
      { id: 'channelStrategy', label: 'Channel Strategy', type: 'textarea', required: true },
      { id: 'partnerProgram', label: 'Partner Program', type: 'textarea', required: false },
      { id: 'logistics', label: 'Logistics & Fulfillment', type: 'textarea', required: true },
    ],
  },
  {
    id: 'sales',
    title: 'Sales & Customer Acquisition',
    description: 'Define your sales strategy and customer acquisition',
    fields: [
      { id: 'salesProcess', label: 'Sales Process', type: 'textarea', required: true },
      { id: 'salesTeam', label: 'Sales Team Structure', type: 'textarea', required: true },
      { id: 'cac', label: 'Customer Acquisition Cost (CAC)', type: 'text', required: true },
      { id: 'ltv', label: 'Lifetime Value (LTV)', type: 'text', required: true },
      {
        id: 'salesForecast',
        label: 'Sales Forecast (12 months)',
        type: 'textarea',
        required: true,
      },
    ],
  },
  {
    id: 'gtmRoadmap',
    title: 'Go-to-Market Roadmap',
    description: 'Create your GTM roadmap with milestones',
    fields: [
      { id: 'phases', label: 'GTM Phases', type: 'textarea', required: true },
      { id: 'milestones', label: 'Key Milestones', type: 'textarea', required: true },
      { id: 'timeline', label: 'Timeline', type: 'textarea', required: true },
      { id: 'resources', label: 'Resource Requirements', type: 'textarea', required: true },
      { id: 'dependencies', label: 'Dependencies & Risks', type: 'textarea', required: true },
    ],
  },
  {
    id: 'financial',
    title: 'Financial Plan & Resource Requirements',
    description: 'Outline financial plan and resource needs',
    fields: [
      { id: 'budget', label: 'Budget Overview', type: 'textarea', required: true },
      { id: 'funding', label: 'Funding Requirements', type: 'textarea', required: true },
      {
        id: 'projections',
        label: 'Financial Projections (12-24 months)',
        type: 'textarea',
        required: true,
      },
      { id: 'resourceNeeds', label: 'Resource Needs', type: 'textarea', required: true },
      { id: 'roi', label: 'Expected ROI', type: 'textarea', required: true },
    ],
  },
  {
    id: 'risk',
    title: 'Risk & Compliance Management',
    description: 'Identify and manage risks and compliance',
    fields: [
      { id: 'risks', label: 'Key Risks', type: 'textarea', required: true },
      { id: 'mitigation', label: 'Risk Mitigation Strategies', type: 'textarea', required: true },
      { id: 'compliance', label: 'Compliance Requirements', type: 'textarea', required: true },
      { id: 'legal', label: 'Legal Considerations', type: 'textarea', required: true },
      { id: 'contingency', label: 'Contingency Plans', type: 'textarea', required: true },
    ],
  },
  {
    id: 'metrics',
    title: 'Performance Metrics & Success Indicators',
    description: 'Define KPIs and success metrics',
    fields: [
      { id: 'kpis', label: 'Key Performance Indicators (KPIs)', type: 'textarea', required: true },
      { id: 'successMetrics', label: 'Success Metrics', type: 'textarea', required: true },
      { id: 'tracking', label: 'Tracking & Reporting', type: 'textarea', required: true },
      { id: 'review', label: 'Review Process', type: 'textarea', required: true },
      { id: 'optimization', label: 'Optimization Strategy', type: 'textarea', required: true },
    ],
  },
];

const GTMStrategyPlan = ({ userRole: propUserRole, userId: propUserId }) => {
  const { currentUser } = useAuth();
  const { userRole: contextUserRole } = useUserRole();
  const effectiveUserRole = propUserRole || contextUserRole || 'client';
  const effectiveUserId = propUserId || currentUser?.uid;

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [previousFormData, setPreviousFormData] = useState({}); // Track previous state for change detection
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [previousUploadedFiles, setPreviousUploadedFiles] = useState({}); // Track previous files
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDraft, setIsDraft] = useState(true);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved'
  const [importOpen, setImportOpen] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [issues, setIssues] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isCommercializedClient, setIsCommercializedClient] = useState(false);
  const [clientBusinessStage, setClientBusinessStage] = useState('');
  const [hasLaunched, setHasLaunched] = useState(false);
  const [unlockedGroups, setUnlockedGroups] = useState([0]); // Track which groups are unlocked (0-based)
  const isInitialLoadRef = useRef(true); // Track if this is the initial data load

  const isClientActor = (effectiveUserRole || '').toLowerCase() === 'client';
  const userCanEdit = canEdit(effectiveUserRole, currentUser?.uid, effectiveUserId);
  const canEditForm = userCanEdit;

  // Calculate progress
  const progress = useMemo(() => {
    const totalFields = STEPS.reduce((sum, step) => sum + step.fields.length, 0);
    const completedFields = STEPS.reduce((sum, step) => {
      const stepFields = step.fields.filter(
        field => formData[`${step.id}_${field.id}`] && formData[`${step.id}_${field.id}`].trim()
      ).length;
      return sum + stepFields;
    }, 0);
    return Math.round((completedFields / totalFields) * 100);
  }, [formData]);

  // Calculate current step from form data
  const currentStepFromData = useMemo(() => {
    for (let i = 0; i < STEPS.length; i++) {
      const step = STEPS[i];
      const hasData = step.fields.some(
        field => formData[`${step.id}_${field.id}`] && formData[`${step.id}_${field.id}`].trim()
      );
      if (!hasData) return i;
    }
    return STEPS.length - 1;
  }, [formData]);

  // Real-time listener for GTM Plan data
  useEffect(() => {
    if (!effectiveUserId || !currentUser?.uid) {
      setLoading(false);
      return;
    }

    const docRef = doc(firestore, 'commercialisation', effectiveUserId, 'gtmPlan', effectiveUserId);

    const unsubscribe = onSnapshot(
      docRef,
      docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const loadedFormData = data.formData || {};
          const loadedUploadedFiles = data.uploadedFiles || {};

          // Set initial previous state (only on first load, not on updates)
          if (isInitialLoadRef.current) {
            setPreviousFormData(JSON.parse(JSON.stringify(loadedFormData)));
            setPreviousUploadedFiles(JSON.parse(JSON.stringify(loadedUploadedFiles)));
            isInitialLoadRef.current = false;
          }

          setFormData(loadedFormData);
          setUploadedFiles(loadedUploadedFiles);
          setIsDraft(data.isDraft !== false);
          setTasks(Array.isArray(data.tasks) ? data.tasks : []);
          setIssues(Array.isArray(data.issues) ? data.issues : []);
          setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);

          // Initialize unlocked groups based on completed steps
          if (data.unlockedGroups && Array.isArray(data.unlockedGroups)) {
            setUnlockedGroups(data.unlockedGroups);
          } else {
            // Calculate unlocked groups based on completion (only required fields)
            const completedSteps = STEPS.filter(step => {
              const requiredFields = step.fields.filter(f => f.required !== false);
              return requiredFields.every(
                field =>
                  loadedFormData[`${step.id}_${field.id}`] &&
                  loadedFormData[`${step.id}_${field.id}`].trim()
              );
            }).length;
            const groupsUnlocked = Math.min(
              Math.floor(completedSteps / 5) + 1,
              Math.ceil(STEPS.length / 5)
            );
            setUnlockedGroups(Array.from({ length: groupsUnlocked }, (_, i) => i));
          }

          // Auto-set current step based on data
          if (currentStepFromData !== currentStep && currentStepFromData < STEPS.length) {
            setCurrentStep(currentStepFromData);
          }
        } else {
          // No data exists, initialize previous state as empty
          if (isInitialLoadRef.current) {
            setPreviousFormData({});
            setPreviousUploadedFiles({});
            isInitialLoadRef.current = false;
          }
        }
        setLoading(false);
      },
      error => {
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [effectiveUserId, currentUser?.uid]);

  useEffect(() => {
    const fetchClientStage = async () => {
      if (!effectiveUserId) return;
      try {
        const [clientSnap, profileSnap] = await Promise.all([
          getDoc(doc(firestore, 'clients', effectiveUserId)),
          getDoc(doc(firestore, 'companyProfiles', effectiveUserId)),
        ]);

        if (clientSnap.exists()) {
          const stage = clientSnap.data()?.businessStage || '';
          setClientBusinessStage(stage);
          const lowerStage = stage.toString().toLowerCase();
          const isCommercial = [
            'commercialized',
            'commercialised',
            'commercialisation',
            'commercialization',
          ].some(flag => lowerStage.includes(flag));
          setIsCommercializedClient(isCommercial);
        } else {
          setClientBusinessStage('');
          setIsCommercializedClient(false);
        }

        if (profileSnap.exists()) {
          setHasLaunched(Boolean(profileSnap.data()?.isLaunched));
        } else {
          setHasLaunched(false);
        }
      } catch (error) {
        setClientBusinessStage('');
        setIsCommercializedClient(false);
        setHasLaunched(false);
      }
    };

    fetchClientStage();
  }, [effectiveUserId]);

  // Helper function to detect if formData has actually changed
  const hasFormDataChanged = (current, previous) => {
    if (Object.keys(current).length !== Object.keys(previous).length) return true;

    for (const key in current) {
      if (current[key] !== previous[key]) {
        // Check if the change is meaningful (not just whitespace)
        const currentTrimmed = String(current[key] || '').trim();
        const previousTrimmed = String(previous[key] || '').trim();
        if (currentTrimmed !== previousTrimmed) {
          return true;
        }
      }
    }

    return false;
  };

  // Helper function to detect if uploadedFiles has changed
  const hasFilesChanged = (current, previous) => {
    const currentKeys = Object.keys(current);
    const previousKeys = Object.keys(previous);

    if (currentKeys.length !== previousKeys.length) return true;

    for (const key of currentKeys) {
      if (!previous[key] || current[key].url !== previous[key].url) {
        return true;
      }
    }

    return false;
  };

  // Auto-save with visual feedback - only when data actually changes
  useEffect(() => {
    if (!effectiveUserId || !currentUser?.uid || loading) return;
    if (!canEditForm) return;

    // Check if data has actually changed
    const formChanged = hasFormDataChanged(formData, previousFormData);
    const filesChanged = hasFilesChanged(uploadedFiles, previousUploadedFiles);

    // Skip auto-save if nothing has changed (including on initial load)
    if (!formChanged && !filesChanged && Object.keys(previousFormData).length > 0) {
      return;
    }

    const autoSaveTimer = setTimeout(async () => {
      try {
        setAutoSaveStatus('saving');

        // Calculate completed steps to determine unlocked groups (only required fields)
        const completedSteps = STEPS.filter(step => {
          const requiredFields = step.fields.filter(f => f.required !== false);
          return requiredFields.every(
            field => formData[`${step.id}_${field.id}`] && formData[`${step.id}_${field.id}`].trim()
          );
        }).length;

        // Determine which groups should be unlocked
        const maxGroupIndex = Math.floor(completedSteps / 5);
        const newUnlockedGroups = Array.from(
          { length: Math.min(maxGroupIndex + 1, Math.ceil(STEPS.length / 5)) },
          (_, i) => i
        );
        setUnlockedGroups(newUnlockedGroups);

        await saveGTMPlan(
          effectiveUserId,
          {
            formData,
            uploadedFiles,
            tasks,
            issues,
            suggestions,
            steps: STEPS.map(step => {
              const requiredFields = step.fields.filter(f => f.required !== false);
              return {
                id: step.id,
                completed: requiredFields.every(
                  field =>
                    formData[`${step.id}_${field.id}`] && formData[`${step.id}_${field.id}`].trim()
                ),
              };
            }),
            currentStep,
            progress,
            unlockedGroups: newUnlockedGroups,
            isDraft: true,
          },
          currentUser
        );

        // Update previous state after successful save
        setPreviousFormData(JSON.parse(JSON.stringify(formData)));
        setPreviousUploadedFiles(JSON.parse(JSON.stringify(uploadedFiles)));

        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      } catch (error) {
        setAutoSaveStatus('idle');
      }
    }, 2000);

    return () => clearTimeout(autoSaveTimer);
  }, [
    formData,
    uploadedFiles,
    previousFormData,
    previousUploadedFiles,
    tasks,
    issues,
    suggestions,
    currentUser?.uid,
    loading,
    progress,
    effectiveUserId,
    currentStep,
    canEditForm,
  ]);

  // Calculate which group the current step belongs to (0-based)
  const getCurrentGroup = stepIndex => Math.floor(stepIndex / 5);

  // Get the steps in the current group
  const getCurrentGroupSteps = groupIndex => {
    const startIndex = groupIndex * 5;
    const endIndex = Math.min(startIndex + 5, STEPS.length);
    return STEPS.slice(startIndex, endIndex);
  };

  // Check if a step is in an unlocked group
  const isStepUnlocked = stepIndex => {
    const groupIndex = getCurrentGroup(stepIndex);
    return unlockedGroups.includes(groupIndex);
  };

  // Check if a step is completed (only required fields must be filled; optional fields are ignored)
  const isStepCompleted = stepIndex => {
    const step = STEPS[stepIndex];
    const requiredFields = step.fields.filter(f => f.required !== false);
    return requiredFields.every(
      field => formData[`${step.id}_${field.id}`] && formData[`${step.id}_${field.id}`].trim()
    );
  };

  // Check if a group is completed (all 5 steps done)
  const isGroupCompleted = groupIndex => {
    const startIndex = groupIndex * 5;
    const endIndex = Math.min(startIndex + 5, STEPS.length);
    for (let i = startIndex; i < endIndex; i++) {
      if (!isStepCompleted(i)) {
        return false;
      }
    }
    return true;
  };

  const handleInputChange = (stepId, fieldId, value) => {
    if (!canEditForm) return;
    const key = `${stepId}_${fieldId}`;
    setFormData(prev => ({
      ...prev,
      [key]: value,
    }));

    // Check if current group is completed and unlock next group
    const currentGroupIndex = getCurrentGroup(currentStep);
    if (
      isGroupCompleted(currentGroupIndex) &&
      currentGroupIndex + 1 < Math.ceil(STEPS.length / 5)
    ) {
      const nextGroupIndex = currentGroupIndex + 1;
      if (!unlockedGroups.includes(nextGroupIndex)) {
        setUnlockedGroups(prev => [...prev, nextGroupIndex]);
      }
    }
  };

  const handleFileUpload = async (stepId, fieldId, file) => {
    if (!canEditForm) {
      toast.error('Access denied');
      return;
    }
    if (!currentUser?.uid) {
      toast.error('Please sign in to upload files');
      return;
    }

    setUploading(true);
    try {
      const filePath = `gtm-strategy/${currentUser.uid}/${stepId}/${fieldId}/${Date.now()}_${file.name}`;
      const fileRef = storageRef(storage, filePath);

      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);

      const key = `${stepId}_${fieldId}`;
      setUploadedFiles(prev => ({
        ...prev,
        [key]: {
          name: file.name,
          url: downloadURL,
          path: filePath,
          uploadedAt: new Date().toISOString(),
        },
      }));

      toast.success('File uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!effectiveUserId || !currentUser?.uid) {
      toast.error('Please sign in to save');
      return;
    }

    if (!canEditForm) {
      toast.error('Access denied');
      return;
    }

    setSaving(true);
    try {
      // Calculate unlocked groups based on completion (only required fields)
      const completedSteps = STEPS.filter(step => {
        const requiredFields = step.fields.filter(f => f.required !== false);
        return requiredFields.every(
          field => formData[`${step.id}_${field.id}`] && formData[`${step.id}_${field.id}`].trim()
        );
      }).length;

      const maxGroupIndex = Math.floor(completedSteps / 5);
      const calculatedUnlockedGroups = Array.from(
        { length: Math.min(maxGroupIndex + 1, Math.ceil(STEPS.length / 5)) },
        (_, i) => i
      );

      await saveGTMPlan(
        effectiveUserId,
        {
          formData,
          uploadedFiles,
          tasks,
          issues,
          suggestions,
          steps: STEPS.map(step => {
            const requiredFields = step.fields.filter(f => f.required !== false);
            return {
              id: step.id,
              completed: requiredFields.every(
                field =>
                  formData[`${step.id}_${field.id}`] && formData[`${step.id}_${field.id}`].trim()
              ),
            };
          }),
          currentStep,
          progress,
          unlockedGroups: calculatedUnlockedGroups,
          isDraft: true,
        },
        currentUser
      );

      setUnlockedGroups(calculatedUnlockedGroups);
      setPreviousFormData(JSON.parse(JSON.stringify(formData)));
      setPreviousUploadedFiles(JSON.parse(JSON.stringify(uploadedFiles)));

      toast.success('Draft saved successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!effectiveUserId || !currentUser?.uid) {
      toast.error('Please sign in to submit');
      return;
    }

    if (!canEditForm) {
      toast.error('Access denied');
      return;
    }

    setSaving(true);
    try {
      // Calculate unlocked groups - all should be unlocked on submit
      const allGroupsUnlocked = Array.from({ length: Math.ceil(STEPS.length / 5) }, (_, i) => i);

      await saveGTMPlan(
        effectiveUserId,
        {
          formData,
          uploadedFiles,
          tasks,
          issues,
          suggestions,
          steps: STEPS.map(step => {
            const requiredFields = step.fields.filter(f => f.required !== false);
            return {
              id: step.id,
              completed: requiredFields.every(
                field =>
                  formData[`${step.id}_${field.id}`] && formData[`${step.id}_${field.id}`].trim()
              ),
            };
          }),
          currentStep,
          progress,
          unlockedGroups: allGroupsUnlocked,
          isDraft: false,
          submittedAt: new Date().toISOString(),
          canvasSummary: generateGTMCanvas(),
        },
        currentUser
      );

      setUnlockedGroups(allGroupsUnlocked);
      setIsDraft(false);
      toast.success('GTM Strategy submitted successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to submit strategy');
    } finally {
      setSaving(false);
    }
  };

  const generateGTMCanvas = () => {
    // Extract key data from formData to create a 1-page summary
    return {
      businessName: formData['executiveSummary_businessName'] || '',
      valueProposition: formData['executiveSummary_valueProposition'] || '',
      targetMarket: formData['executiveSummary_targetMarket'] || '',
      pricing: formData['pricing_pricingModel'] || '',
      channels: formData['distribution_distributionChannels'] || '',
      customerAcquisition: formData['sales_salesProcess'] || '',
      revenue: formData['financial_revenueModel'] || '',
      costs: formData['financial_budget'] || '',
      keyMetrics: formData['metrics_kpis'] || '',
      generatedAt: new Date().toISOString(),
    };
  };

  const handleExport = () => {
    const data = {
      timestamp: new Date().toISOString(),
      progress,
      formData,
      uploadedFiles,
      tasks,
      issues,
      suggestions,
      canvasSummary: generateGTMCanvas(),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gtm-strategy-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success('Data exported successfully!');
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      const nextStep = currentStep + 1;
      const currentGroupIndex = getCurrentGroup(currentStep);
      const nextGroupIndex = getCurrentGroup(nextStep);

      // If moving to next group, check if current group is completed
      if (nextGroupIndex > currentGroupIndex) {
        if (!isGroupCompleted(currentGroupIndex)) {
          toast.error('Please complete all steps in the current group before proceeding');
          return;
        }

        // Unlock next group if not already unlocked
        if (!unlockedGroups.includes(nextGroupIndex)) {
          setUnlockedGroups(prev => [...prev, nextGroupIndex]);
        }
      }

      // Check if next step is unlocked
      if (isStepUnlocked(nextStep)) {
        setCurrentStep(nextStep);
      } else {
        toast.error('Please complete the current group before proceeding');
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleDotClick = dotIndex => {
    const currentGroupIndex = getCurrentGroup(currentStep);
    const targetStepIndex = currentGroupIndex * 5 + dotIndex;

    // Only allow clicking if the step exists and is unlocked
    if (targetStepIndex < STEPS.length && isStepUnlocked(targetStepIndex)) {
      setCurrentStep(targetStepIndex);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
          <p className="text-sm text-gray-500">Loading strategy form...</p>
        </div>
      </div>
    );
  }

  const currentStepData = STEPS[currentStep];

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 md:py-8 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          {/* Main Header Section */}
          <div className="mb-6 border-b border-gray-100 pb-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              {/* Title Section */}
              <div className="flex-1">
                <motion.h1
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                  className="mb-2 text-2xl font-bold text-black sm:text-3xl"
                >
                  Go-to-Market Strategy
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.15 }}
                  className="text-sm text-gray-500 sm:text-base"
                >
                  Develop and access your comprehensive go-to-market strategy and commercialisation
                  plan
                </motion.p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Auto-save indicator */}
                {canEdit(effectiveUserRole, currentUser?.uid, effectiveUserId) && (
                  <AnimatePresence mode="wait">
                    {autoSaveStatus !== 'idle' && (
                      <motion.div
                        key={autoSaveStatus}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                        className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium"
                      >
                        {autoSaveStatus === 'saving' && (
                          <>
                            <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500" />
                            <span className="text-gray-600">Saving...</span>
                          </>
                        )}
                        {autoSaveStatus === 'saved' && (
                          <>
                            <HiCheckCircle className="h-3.5 w-3.5 text-green-500" />
                            <span className="text-gray-700">Saved</span>
                          </>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}

                {/* Secondary Actions */}
                <div className="flex items-center gap-2">
                  {canEdit(effectiveUserRole, currentUser?.uid, effectiveUserId) && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleSaveDraft}
                      disabled={saving || !canEditForm}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-all hover:border-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:py-2 sm:text-sm"
                    >
                      <HiDocumentArrowDown className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">
                        {saving ? 'Saving...' : 'Save Draft'}
                      </span>
                      <span className="sm:hidden">Save</span>
                    </motion.button>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleExport}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-all hover:border-gray-400 hover:bg-gray-50 sm:px-4 sm:py-2 sm:text-sm"
                  >
                    <HiArrowDownTray className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Export</span>
                  </motion.button>
                  {canEdit(effectiveUserRole, currentUser?.uid, effectiveUserId) && (
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setImportOpen(true)}
                      disabled={!canEditForm}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-all hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 sm:px-4 sm:py-2 sm:text-sm"
                    >
                      <HiSparkles className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      <span className="hidden sm:inline">Import GTM</span>
                      <span className="sm:hidden">Import</span>
                    </motion.button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Info Banner */}
          {isClientActor && (isCommercializedClient || hasLaunched) && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="rounded-lg border border-gray-200 bg-gray-50/50 px-4 py-3 text-sm"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex-shrink-0">
                  <HiCheckCircle className="h-4 w-4 text-gray-400" />
                </div>
                <div className="flex-1 space-y-1">
                  <p className="font-medium text-gray-900">Commercialisation and launch tracking</p>
                  <p className="text-xs leading-relaxed text-gray-600">
                    {hasLaunched ? (
                      <>
                        Your launch status is <span className="font-medium">Launched</span>. Keep
                        refining your GTM plan and coordinate major updates with your advisor.
                      </>
                    ) : (
                      <>
                        Current business stage:{' '}
                        <span className="font-medium">
                          {clientBusinessStage || 'Commercialised'}
                        </span>
                        . Continue building your plan to prepare for official launch.
                      </>
                    )}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Progress Bar */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Progress</h2>
              <p className="mt-1 text-sm text-gray-600">
                Step {currentStep + 1} of {STEPS.length}
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">{progress}%</div>
              <div className="text-sm text-gray-600">Complete</div>
            </div>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
              className="h-full bg-blue-600"
            />
          </div>
        </div>

        {/* Group Progress with 5 Dots */}
        {(() => {
          const currentGroupIndex = getCurrentGroup(currentStep);
          const currentGroupSteps = getCurrentGroupSteps(currentGroupIndex);
          const relativeStepInGroup = currentStep % 5;

          return (
            <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Group {currentGroupIndex + 1} of {Math.ceil(STEPS.length / 5)}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    {currentGroupSteps[0].title} -{' '}
                    {currentGroupSteps[currentGroupSteps.length - 1].title}
                  </p>
                </div>
                {currentGroupIndex + 1 < Math.ceil(STEPS.length / 5) && (
                  <div className="text-right">
                    <p className="text-sm text-gray-600">
                      {isGroupCompleted(currentGroupIndex)
                        ? 'Group completed! Next group unlocked.'
                        : `${5 - currentGroupSteps.filter((_, idx) => isStepCompleted(currentGroupIndex * 5 + idx)).length} steps remaining`}
                    </p>
                  </div>
                )}
              </div>

              {/* 5 Dots Progress Indicator */}
              <div className="flex items-center justify-center gap-4">
                {[0, 1, 2, 3, 4].map(dotIndex => {
                  const stepIndex = currentGroupIndex * 5 + dotIndex;
                  const stepExists = stepIndex < STEPS.length;
                  const isCompleted = stepExists && isStepCompleted(stepIndex);
                  const isCurrent = stepIndex === currentStep;
                  const isUnlocked = isStepUnlocked(stepIndex);

                  if (!stepExists) {
                    return null; // Don't show dots for non-existent steps
                  }

                  return (
                    <button
                      key={dotIndex}
                      onClick={() => handleDotClick(dotIndex)}
                      disabled={!isUnlocked}
                      className={`relative flex items-center justify-center transition-all ${
                        !isUnlocked
                          ? 'cursor-not-allowed opacity-40'
                          : 'cursor-pointer hover:scale-110'
                      }`}
                      title={`Step ${stepIndex + 1}: ${STEPS[stepIndex].title}`}
                    >
                      <div
                        className={`h-4 w-4 rounded-full transition-all ${
                          isCurrent
                            ? 'h-5 w-5 bg-blue-600 ring-4 ring-blue-200'
                            : isCompleted
                              ? 'bg-green-500'
                              : isUnlocked
                                ? 'bg-gray-300 hover:bg-gray-400'
                                : 'bg-gray-200'
                        }`}
                      />
                      {isCompleted && isCurrent && (
                        <HiCheckCircle className="absolute h-6 w-6 text-white" />
                      )}
                      {isCompleted && !isCurrent && (
                        <HiCheckCircle className="absolute h-5 w-5 text-white" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Current step indicator */}
              <div className="mt-4 text-center">
                <p className="text-sm font-medium text-gray-700">
                  Step {relativeStepInGroup + 1} of {currentGroupSteps.length}:{' '}
                  {STEPS[currentStep].title}
                </p>
              </div>

              {/* Show navigation hint for next group */}
              {isGroupCompleted(currentGroupIndex) &&
                currentGroupIndex + 1 < Math.ceil(STEPS.length / 5) && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 rounded-lg border border-green-200 bg-green-50 p-3 text-center"
                  >
                    <p className="text-sm font-medium text-green-800">
                      ✓ Group completed! Click "Next" to continue to Group {currentGroupIndex + 2}
                    </p>
                  </motion.div>
                )}
            </div>
          );
        })()}

        {/* Current Step Form */}
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
        >
          <div className="mb-6">
            <h2 className="mb-2 text-2xl font-bold text-black">{currentStepData.title}</h2>
            <p className="text-gray-600">{currentStepData.description}</p>
          </div>

          <div className="space-y-6">
            {currentStepData.fields.map(field => {
              const key = `${currentStepData.id}_${field.id}`;
              const value = formData[key] || '';
              const file = uploadedFiles[key];

              return (
                <div key={field.id}>
                  <label className="mb-2 block text-sm font-medium text-gray-900">
                    {field.label}
                    {field.required && <span className="text-red-500"> *</span>}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={value}
                      onChange={e =>
                        handleInputChange(currentStepData.id, field.id, e.target.value)
                      }
                      rows={6}
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      disabled={!canEditForm}
                    />
                  ) : (
                    <input
                      type={field.type}
                      value={value}
                      onChange={e =>
                        handleInputChange(currentStepData.id, field.id, e.target.value)
                      }
                      className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                      placeholder={`Enter ${field.label.toLowerCase()}`}
                      disabled={!canEditForm}
                    />
                  )}

                  {/* File Upload */}
                  <div className="mt-2">
                    <label
                      className={`flex items-center gap-2 text-sm text-gray-600 ${
                        canEditForm ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                      }`}
                    >
                      <HiCloudArrowUp className="h-4 w-4" />
                      <span>Upload file</span>
                      <input
                        type="file"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleFileUpload(currentStepData.id, field.id, file);
                          }
                        }}
                        className="hidden"
                        disabled={uploading || !canEditForm}
                      />
                    </label>
                    {file && (
                      <div className="mt-2 flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                        <HiDocumentText className="h-4 w-4 text-gray-600" />
                        <span className="flex-1 text-sm text-gray-700">{file.name}</span>
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-blue-600 hover:underline"
                        >
                          View
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Tasks Tracker and Insights */}
        <div className="mt-8 grid gap-6">
          <GTMTaskTracker
            tasks={tasks}
            setTasks={canEditForm ? setTasks : () => {}}
            onChange={canEditForm ? setTasks : () => {}}
            readOnly={!canEditForm}
          />
          <GTMInsights issues={issues} suggestions={suggestions} />
        </div>

        {/* Navigation Buttons */}
        <div className="mt-8 flex items-center justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <HiChevronLeft className="h-4 w-4" />
            Previous
          </button>

          {currentStep === STEPS.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={saving || !canEditForm}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Submitting...' : 'Submit & Generate Canvas'}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={(() => {
                const nextStep = currentStep + 1;
                const currentGroupIndex = getCurrentGroup(currentStep);
                const nextGroupIndex = getCurrentGroup(nextStep);

                // Disable if moving to next group and current group is not completed
                if (nextGroupIndex > currentGroupIndex && !isGroupCompleted(currentGroupIndex)) {
                  return true;
                }

                // Disable if next step is not unlocked
                return !isStepUnlocked(nextStep);
              })()}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {(() => {
                const nextStep = currentStep + 1;
                const currentGroupIndex = getCurrentGroup(currentStep);
                const nextGroupIndex = getCurrentGroup(nextStep);

                if (nextGroupIndex > currentGroupIndex) {
                  return `Next Group (${nextGroupIndex + 1})`;
                }
                return 'Next';
              })()}
              <HiChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Comment Section */}
        {effectiveUserId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8"
          >
            <CommentSection userId={effectiveUserId} checklistType="gtm" />
          </motion.div>
        )}
        <GTMImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={({
            formData: extracted,
            tasks: extractedTasks,
            issues: extractedIssues,
            suggestions: extractedSuggestions,
          }) => {
            if (!canEditForm) return;
            setFormData(prev => ({ ...extracted, ...prev }));
            if (Array.isArray(extractedTasks) && extractedTasks.length) setTasks(extractedTasks);
            if (Array.isArray(extractedIssues)) setIssues(extractedIssues);
            if (Array.isArray(extractedSuggestions)) setSuggestions(extractedSuggestions);
          }}
        />
      </div>
    </div>
  );
};

export default GTMStrategyPlan;
